import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import { money } from "@/features/inventory/constants";
import type {
  InventoryBatch,
  InventoryBatchLink,
  InventoryBatchListRow,
  InventoryMovement,
} from "@/features/inventory/types";

function hydrateBatch(raw: Record<string, unknown>): InventoryBatch {
  return {
    id: String(raw.id),
    batch_no: String(raw.batch_no ?? ""),
    material_id: String(raw.material_id),
    material_grade_id: (raw.material_grade_id as string | null) ?? null,
    shape_size_id: (raw.shape_size_id as string | null) ?? null,
    location_id: String(raw.location_id),
    quantity: money(raw.quantity),
    weight: money(raw.weight),
    uom_id: (raw.uom_id as string | null) ?? null,
    thickness: raw.thickness == null ? null : money(raw.thickness),
    width: raw.width == null ? null : money(raw.width),
    length: raw.length == null ? null : money(raw.length),
    wt_per_m: raw.wt_per_m == null ? null : money(raw.wt_per_m),
    purchase_order_id: (raw.purchase_order_id as string | null) ?? null,
    purchase_order_line_id: (raw.purchase_order_line_id as string | null) ?? null,
    vendor_id: (raw.vendor_id as string | null) ?? null,
    batch_status: raw.batch_status as InventoryBatch["batch_status"],
    quality_status: raw.quality_status as InventoryBatch["quality_status"],
    inspected_by: (raw.inspected_by as string | null) ?? null,
    inspected_at: (raw.inspected_at as string | null) ?? null,
    qc_notes: (raw.qc_notes as string | null) ?? null,
    received_at: String(raw.received_at ?? raw.created_at),
    notes: (raw.notes as string | null) ?? null,
    created_by: (raw.created_by as string | null) ?? null,
    created_at: String(raw.created_at),
    updated_at: String(raw.updated_at),
    mother_batch_id: (raw.mother_batch_id as string | null) ?? null,
    last_process_type_id: (raw.last_process_type_id as string | null) ?? null,
    process_path: (raw.process_path as string | null) ?? null,
  };
}

export function useInventoryBatches(filters?: {
  materialId?: string;
  locationId?: string;
  batchStatus?: string;
  /** When set, overrides single batchStatus (planning: in_stock + reserved). */
  batchStatuses?: string[];
}) {
  return useQuery({
    queryKey: [
      "inventory_batches",
      filters?.materialId ?? "all",
      filters?.locationId ?? "all",
      filters?.batchStatuses?.join(",") ?? filters?.batchStatus ?? "all",
    ],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<InventoryBatchListRow[]> => {
      let q = supabase
        .from("inventory_batches")
        .select(
          `*,
          materials(name),
          material_grades(name),
          shape_sizes(size_label),
          stock_locations(code, name),
          vendors(code, name),
          uoms(code),
          purchase_orders(po_number),
          process_types!inventory_batches_last_process_type_id_fkey(code, name)`,
        )
        .order("received_at", { ascending: false })
        .order("created_at", { ascending: false });

      if (filters?.materialId) q = q.eq("material_id", filters.materialId);
      if (filters?.locationId) q = q.eq("location_id", filters.locationId);
      if (filters?.batchStatuses?.length) {
        q = q.in("batch_status", filters.batchStatuses);
      } else if (filters?.batchStatus && filters.batchStatus !== "all") {
        q = q.eq("batch_status", filters.batchStatus);
      }

      const { data, error } = await q;
      if (error) throw error;

      return (data ?? []).map((row) => {
        const raw = row as Record<string, unknown>;
        const material = raw.materials as { name?: string } | null;
        const grade = raw.material_grades as { name?: string } | null;
        const shape = raw.shape_sizes as { size_label?: string } | null;
        const loc = raw.stock_locations as { code?: string; name?: string } | null;
        const vendor = raw.vendors as { code?: string; name?: string } | null;
        const uom = raw.uoms as { code?: string } | null;
        const po = raw.purchase_orders as { po_number?: string } | null;
        const pt = raw.process_types as { code?: string; name?: string } | null;
        const {
          materials: _m,
          material_grades: _g,
          shape_sizes: _s,
          stock_locations: _l,
          vendors: _v,
          uoms: _u,
          purchase_orders: _p,
          process_types: _pt,
          ...rest
        } = raw;
        return {
          ...hydrateBatch(rest),
          material_name: material?.name ?? null,
          grade_name: grade?.name ?? null,
          shape_label: shape?.size_label ?? null,
          location_code: loc?.code ?? null,
          location_name: loc?.name ?? null,
          vendor_code: vendor?.code ?? null,
          vendor_name: vendor?.name ?? null,
          uom_code: uom?.code ?? null,
          po_number: po?.po_number ?? null,
          last_process_code: pt?.code ?? null,
          last_process_name: pt?.name ?? null,
        };
      });
    },
  });
}

export function useInventoryBatch(id?: string) {
  return useQuery({
    queryKey: ["inventory_batches", id],
    enabled: isSupabaseConfigured && Boolean(id),
    queryFn: async (): Promise<InventoryBatchListRow> => {
      const { data, error } = await supabase
        .from("inventory_batches")
        .select(
          `*,
          materials(name),
          material_grades(name),
          shape_sizes(size_label),
          stock_locations(code, name),
          vendors(code, name),
          uoms(code),
          purchase_orders(po_number),
          process_types!inventory_batches_last_process_type_id_fkey(code, name),
          mother:inventory_batches!inventory_batches_mother_batch_id_fkey(batch_no)`,
        )
        .eq("id", id!)
        .single();
      if (error) throw error;

      const raw = data as Record<string, unknown>;
      const material = raw.materials as { name?: string } | null;
      const grade = raw.material_grades as { name?: string } | null;
      const shape = raw.shape_sizes as { size_label?: string } | null;
      const loc = raw.stock_locations as { code?: string; name?: string } | null;
      const vendor = raw.vendors as { code?: string; name?: string } | null;
      const uom = raw.uoms as { code?: string } | null;
      const po = raw.purchase_orders as { po_number?: string } | null;
      const pt = raw.process_types as { code?: string; name?: string } | null;
      const mother = raw.mother as { batch_no?: string } | null;
      const {
        materials: _m,
        material_grades: _g,
        shape_sizes: _s,
        stock_locations: _l,
        vendors: _v,
        uoms: _u,
        purchase_orders: _p,
        process_types: _pt,
        mother: _mo,
        ...rest
      } = raw;
      return {
        ...hydrateBatch(rest),
        material_name: material?.name ?? null,
        grade_name: grade?.name ?? null,
        shape_label: shape?.size_label ?? null,
        location_code: loc?.code ?? null,
        location_name: loc?.name ?? null,
        vendor_code: vendor?.code ?? null,
        vendor_name: vendor?.name ?? null,
        uom_code: uom?.code ?? null,
        po_number: po?.po_number ?? null,
        last_process_code: pt?.code ?? null,
        last_process_name: pt?.name ?? null,
        mother_batch_no: mother?.batch_no ?? null,
      };
    },
  });
}

export function useBatchGenealogy(batchId?: string) {
  return useQuery({
    queryKey: ["inventory_batch_genealogy", batchId],
    enabled: isSupabaseConfigured && Boolean(batchId),
    queryFn: async (): Promise<{
      children: InventoryBatchLink[];
      parents: InventoryBatchLink[];
      siblings: { id: string; batch_no: string; process_path: string | null }[];
    }> => {
      const [{ data: asChild, error: e1 }, { data: asParent, error: e2 }, { data: batch, error: e3 }] =
        await Promise.all([
          supabase
            .from("inventory_batch_links")
            .select(
              `*, process_types(code, name), parent:inventory_batches!inventory_batch_links_parent_batch_id_fkey(batch_no)`,
            )
            .eq("child_batch_id", batchId!),
          supabase
            .from("inventory_batch_links")
            .select(
              `*, process_types(code, name), child:inventory_batches!inventory_batch_links_child_batch_id_fkey(batch_no)`,
            )
            .eq("parent_batch_id", batchId!),
          supabase
            .from("inventory_batches")
            .select("id, mother_batch_id")
            .eq("id", batchId!)
            .single(),
        ]);
      if (e1) throw e1;
      if (e2) throw e2;
      if (e3) throw e3;

      const mapLink = (row: Record<string, unknown>, side: "parent" | "child"): InventoryBatchLink => {
        const pt = row.process_types as { code?: string; name?: string } | null;
        const p = row.parent as { batch_no?: string } | null;
        const c = row.child as { batch_no?: string } | null;
        return {
          id: String(row.id),
          parent_batch_id: String(row.parent_batch_id),
          child_batch_id: String(row.child_batch_id),
          link_kind: row.link_kind as InventoryBatchLink["link_kind"],
          process_type_id: (row.process_type_id as string | null) ?? null,
          process_stage_run_id: (row.process_stage_run_id as string | null) ?? null,
          created_at: String(row.created_at),
          parent_batch_no: side === "parent" ? (p?.batch_no ?? null) : null,
          child_batch_no: side === "child" ? (c?.batch_no ?? null) : null,
          process_code: pt?.code ?? null,
          process_name: pt?.name ?? null,
        };
      };

      const parents = (asChild ?? []).map((r) => mapLink(r as Record<string, unknown>, "parent"));
      const children = (asParent ?? []).map((r) => mapLink(r as Record<string, unknown>, "child"));

      let siblings: { id: string; batch_no: string; process_path: string | null }[] = [];
      const motherId = batch?.mother_batch_id as string | null;
      if (motherId) {
        const { data: sibs, error: e4 } = await supabase
          .from("inventory_batches")
          .select("id, batch_no, process_path")
          .eq("mother_batch_id", motherId)
          .neq("id", batchId!)
          .order("batch_no", { ascending: true });
        if (e4) throw e4;
        siblings = (sibs ?? []).map((s) => ({
          id: String(s.id),
          batch_no: String(s.batch_no),
          process_path: (s.process_path as string | null) ?? null,
        }));
      }

      return { children, parents, siblings };
    },
  });
}

export function useInventoryMovements(batchId?: string) {
  return useQuery({
    queryKey: ["inventory_movements", batchId],
    enabled: isSupabaseConfigured && Boolean(batchId),
    queryFn: async (): Promise<InventoryMovement[]> => {
      const { data, error } = await supabase
        .from("inventory_movements")
        .select("*")
        .eq("inventory_batch_id", batchId!)
        .order("movement_at", { ascending: false })
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []).map((row) => {
        const r = row as Record<string, unknown>;
        return {
          id: String(r.id),
          inventory_batch_id: String(r.inventory_batch_id),
          movement_at: String(r.movement_at),
          kind: r.kind as InventoryMovement["kind"],
          quantity_delta: money(r.quantity_delta),
          weight_delta: money(r.weight_delta),
          quantity_after: money(r.quantity_after),
          weight_after: money(r.weight_after),
          from_location_id: (r.from_location_id as string | null) ?? null,
          to_location_id: (r.to_location_id as string | null) ?? null,
          source_type: (r.source_type as string | null) ?? null,
          source_id: (r.source_id as string | null) ?? null,
          notes: (r.notes as string | null) ?? null,
          created_by: (r.created_by as string | null) ?? null,
          created_at: String(r.created_at),
        };
      });
    },
  });
}

export type BatchGoodsReceiptTrail = {
  receipt_id: string;
  receipt_no: string;
  purchase_order_id: string | null;
  received_at: string;
  quantity: number;
  weight: number;
};

function mapBatchListRow(row: Record<string, unknown>): InventoryBatchListRow {
  const material = row.materials as { name?: string } | null;
  const grade = row.material_grades as { name?: string } | null;
  const shape = row.shape_sizes as { size_label?: string } | null;
  const loc = row.stock_locations as { code?: string; name?: string } | null;
  const vendor = row.vendors as { code?: string; name?: string } | null;
  const uom = row.uoms as { code?: string } | null;
  const po = row.purchase_orders as { po_number?: string } | null;
  const {
    materials: _m,
    material_grades: _g,
    shape_sizes: _s,
    stock_locations: _l,
    vendors: _v,
    uoms: _u,
    purchase_orders: _p,
    ...rest
  } = row;
  return {
    ...hydrateBatch(rest),
    material_name: material?.name ?? null,
    grade_name: grade?.name ?? null,
    shape_label: shape?.size_label ?? null,
    location_code: loc?.code ?? null,
    location_name: loc?.name ?? null,
    vendor_code: vendor?.code ?? null,
    vendor_name: vendor?.name ?? null,
    uom_code: uom?.code ?? null,
    po_number: po?.po_number ?? null,
  };
}

const BATCH_SELECT = `*,
  materials(name),
  material_grades(name),
  shape_sizes(size_label),
  stock_locations(code, name),
  vendors(code, name),
  uoms(code),
  purchase_orders(po_number)`;

/** Typeahead / search for lookup — batch_no or material name */
export function useInventoryBatchSearch(query: string) {
  const q = query.trim();
  return useQuery({
    queryKey: ["inventory_batches", "search", q],
    enabled: isSupabaseConfigured && q.length >= 1,
    queryFn: async (): Promise<InventoryBatchListRow[]> => {
      const { data: mats } = await supabase
        .from("materials")
        .select("id")
        .ilike("name", `%${q}%`)
        .limit(20);
      const materialIds = (mats ?? []).map((m) => m.id as string);

      let byNo = supabase
        .from("inventory_batches")
        .select(BATCH_SELECT)
        .ilike("batch_no", `%${q}%`)
        .order("received_at", { ascending: false })
        .limit(40);
      const { data: noRows, error: noErr } = await byNo;
      if (noErr) throw noErr;

      let matRows: Record<string, unknown>[] = [];
      if (materialIds.length) {
        const { data, error } = await supabase
          .from("inventory_batches")
          .select(BATCH_SELECT)
          .in("material_id", materialIds)
          .order("received_at", { ascending: false })
          .limit(40);
        if (error) throw error;
        matRows = (data ?? []) as Record<string, unknown>[];
      }

      const map = new Map<string, InventoryBatchListRow>();
      for (const row of [...(noRows ?? []), ...matRows]) {
        const mapped = mapBatchListRow(row as Record<string, unknown>);
        map.set(mapped.id, mapped);
      }
      return [...map.values()].slice(0, 40);
    },
  });
}

export function useBatchGoodsReceipts(batchId?: string) {
  return useQuery({
    queryKey: ["goods_receipt_lines", "by_batch", batchId],
    enabled: isSupabaseConfigured && Boolean(batchId),
    queryFn: async (): Promise<BatchGoodsReceiptTrail[]> => {
      const { data, error } = await supabase
        .from("goods_receipt_lines")
        .select("quantity, weight, goods_receipts(id, receipt_no, purchase_order_id, received_at)")
        .eq("inventory_batch_id", batchId!);
      if (error) throw error;
      return (data ?? []).map((row) => {
        const r = row as Record<string, unknown>;
        const gr = r.goods_receipts as {
          id?: string;
          receipt_no?: string;
          purchase_order_id?: string | null;
          received_at?: string;
        } | null;
        return {
          receipt_id: String(gr?.id ?? ""),
          receipt_no: String(gr?.receipt_no ?? "—"),
          purchase_order_id: gr?.purchase_order_id ?? null,
          received_at: String(gr?.received_at ?? ""),
          quantity: money(r.quantity),
          weight: money(r.weight),
        };
      });
    },
  });
}

/** Move an in-stock batch to another location (location-only transfer). */
export function useTransferBatch() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      batchId,
      toLocationId,
      notes,
    }: {
      batchId: string;
      toLocationId: string;
      notes?: string;
    }) => {
      if (!toLocationId.trim()) throw new Error("Destination location is required");

      const { data: batch, error: batchErr } = await supabase
        .from("inventory_batches")
        .select("id, location_id, quantity, weight, batch_status")
        .eq("id", batchId)
        .single();
      if (batchErr) throw batchErr;
      if (batch.batch_status !== "in_stock") {
        throw new Error("Only in-stock batches can be transferred");
      }
      if (String(batch.location_id) === toLocationId) {
        throw new Error("Batch is already at that location");
      }

      const fromLocationId = String(batch.location_id);
      const { error: updErr } = await supabase
        .from("inventory_batches")
        .update({
          location_id: toLocationId,
          updated_at: new Date().toISOString(),
        })
        .eq("id", batchId);
      if (updErr) throw updErr;

      const { error: moveErr } = await supabase.from("inventory_movements").insert({
        inventory_batch_id: batchId,
        movement_at: new Date().toISOString(),
        kind: "transfer",
        quantity_delta: 0,
        weight_delta: 0,
        quantity_after: money(batch.quantity),
        weight_after: money(batch.weight),
        from_location_id: fromLocationId,
        to_location_id: toLocationId,
        notes: notes?.trim() || "Location transfer",
        created_by: user?.id ?? null,
      });
      if (moveErr) {
        await supabase
          .from("inventory_batches")
          .update({ location_id: fromLocationId, updated_at: new Date().toISOString() })
          .eq("id", batchId);
        throw moveErr;
      }
    },
    onSuccess: async (_d, vars) => {
      await queryClient.invalidateQueries({ queryKey: ["inventory_batches"] });
      await queryClient.invalidateQueries({ queryKey: ["inventory_batches", vars.batchId] });
      await queryClient.invalidateQueries({ queryKey: ["inventory_movements", vars.batchId] });
      toast.success("Batch transferred");
    },
    onError: (e: Error) => toast.error(e.message || "Transfer failed"),
  });
}
